import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	BuildingOfficeIcon,
	UserIcon,
	EnvelopeIcon,
	PhoneIcon,
	MapPinIcon,
	GlobeAltIcon,
	BriefcaseIcon,
	PencilIcon,
	TrashIcon,
} from '@heroicons/react/24/outline';

import { getContact, deleteContact } from '../../api/contacts';
import { getUserFullName } from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { StatusBadge } from '../../components/nextgen';
import { AvatarGenerated } from '../../components/Avatar';
import Button from '../../components/Button';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSettings from '../../hooks/useSettings';
import { parseAssetTemplate } from '../../components/nextgen/AssetSelectorBeta';

const VENDOR_TYPES = {
	manufacturer: 'Manufacturer',
	service_vendor: 'Service Vendor',
	distributor: 'Distributor',
	other: 'Other',
};

export default () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const {
		value: contact,
		execute: refreshContact,
		status,
	} = useAsync(getContact, { immediate: false });

	const { execute: onDeleteContact } = useAsync(deleteContact, {
		immediate: false,
	});

	const { setOpen: setDeleteModalOpen, setClose: setDeleteModalClose } =
		useModal('deleteModal');

	const { setOpen: setContactModalOpen } = useModal('contactModal');

	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	useEffect(() => {
		if (id) {
			refreshContact({ id });
		}
	}, [id, refreshContact]);

	// Get display name
	const getDisplayName = () => {
		if (!contact) return '';
		return contact.type === 'company'
			? contact.companyName || 'Unnamed Company'
			: getUserFullName(contact) || 'Unnamed Contact';
	};

	// Get vendor type label
	const getVendorType = () => {
		if (!contact) return '';
		if (contact.vendorType && VENDOR_TYPES[contact.vendorType]) {
			return VENDOR_TYPES[contact.vendorType];
		}
		return contact.type === 'company' ? 'Company' : 'Individual';
	};

	return (
		<MainContent maxWidth="w-full bg-gray-50 min-h-screen">
			{status === 'pending' && !contact ? (
				<div className="max-w-5xl mx-auto">
					<LoadingSkeleton />
				</div>
			) : !contact ? (
				<div className="text-center py-12">
					<p className="text-sm text-gray-500">Contact not found</p>
				</div>
			) : (
				<div className="max-w-5xl mx-auto">
					{/* Header */}
					<PageHeading
						actions={[
							{
								name: 'Edit',
								type: 'secondary',
								icon: PencilIcon,
								onClick: () =>
									setContactModalOpen({
										id,
										onSuccess: () => refreshContact({ id }),
									}),
							},
							{
								render: () => (
									<EllipsisDropdown>
										<EllipsisDropdownItem
											danger
											onClick={() =>
												setDeleteModalOpen({
													title:
														'Are you sure you want to delete this contact?',
													description:
														'This action cannot be undone. All data associated with this contact will be permanently removed.',
													confirmText: 'Delete Contact',
													onConfirm: () =>
														onDeleteContact(contact?.id, () => {
															setDeleteModalClose();
															navigate('/manage/directory');
														}),
												})
											}
										>
											<div className="flex items-center">
												<TrashIcon className="mr-3 h-5 w-5" />
												Delete Contact
											</div>
										</EllipsisDropdownItem>
									</EllipsisDropdown>
								),
							},
						]}
						divider={false}
					>
						<button
							onClick={() => navigate('/manage/directory')}
							className="text-sm text-gray-500 hover:text-gray-700 font-normal"
						>
							← Back to Directory
						</button>
					</PageHeading>

					<div className="">
						<div className="grid grid-cols-5 gap-x-6">
							<div className="col-span-2">
								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center gap-3 py-4 px-6 border-b border-gray-200">
										{contact.type === 'person' ? (
											<AvatarGenerated
												size="sm"
												id={getUserFullName(contact)}
											/>
										) : (
											<AvatarGenerated size="sm" id={contact.companyName} />
										)}
										<h3 className="text-sm font-medium text-gray-900">
											{getDisplayName()}
										</h3>
									</div>

									<dl className="grid grid-cols-1 gap-y-4 px-6 pb-6">
										{contact.type === 'person' && (
											<div className="flex items-start gap-3">
												<BriefcaseIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
												<div className="min-w-0 flex-1">
													<dt className="text-xs font-medium text-gray-500">
														Title
													</dt>
													<dd className="mt-1 text-sm text-gray-900 break-words">
														{contact.title ? (
															contact.title
														) : (
															<span className="text-gray-400">—</span>
														)}
													</dd>
												</div>
											</div>
										)}

										<div className="flex items-start gap-3">
											<EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Email
												</dt>
												<dd className="mt-1 text-sm text-gray-900 break-words">
													{contact.email ? (
														<a
															href={`mailto:${contact.email}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{contact.email}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Phone
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{contact.phoneNumber ? (
														<a
															href={`tel:${contact.phoneNumber}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{contact.phoneNumber}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<MapPinIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Location
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{contact.location || (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<GlobeAltIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Website
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{contact.url ? (
														<a
															href={contact.url}
															target="_blank"
															rel="noopener noreferrer"
															className="text-indigo-600 hover:text-indigo-700"
														>
															{contact.url}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>
									</dl>
								</div>

								{/* Company section for persons */}
								{contact.type === 'person' && contact.company && (
									<div className="bg-white border-gray-200 border rounded-lg mt-6">
										<div className="py-4 px-6 border-b border-gray-200">
											<h3 className="text-sm font-medium text-gray-900 flex items-center">
												Company
											</h3>
										</div>
										<div
											className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
											onClick={() =>
												navigate(`/manage/directory/${contact.company.id}`)
											}
										>
											<div className="flex items-center gap-3">
												<AvatarGenerated
													size="sm"
													id={contact.company.companyName}
												/>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-gray-900">
														{contact.company.companyName}
													</p>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Employees section for companies */}
								{contact.type === 'company' && (
									<div className="bg-white border-gray-200 border rounded-lg mt-6">
										<div className="py-4 px-6 border-b border-gray-200">
											<h3 className="text-sm font-medium text-gray-900 flex items-center">
												Employees{' '}
												{contact.contacts &&
													contact.contacts.length > 0 &&
													`(${contact.contacts.length})`}
											</h3>
										</div>
										{contact.contacts && contact.contacts.length > 0 ? (
											<ul className="divide-y divide-gray-200  m-0 p-0 !mt-0">
												{contact.contacts.map((person) => (
													<li
														key={person.id}
														className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
														onClick={() =>
															navigate(`/manage/directory/${person.id}`)
														}
													>
														<div className="flex items-center gap-3">
															<AvatarGenerated
																size="sm"
																id={getUserFullName(person)}
															/>
															<div className="min-w-0 flex-1">
																<p className="text-sm font-medium text-gray-900">
																	{getUserFullName(person)}
																</p>
																{person.title && (
																	<p className="text-xs text-gray-500">
																		{person.title}
																	</p>
																)}
															</div>
															<div className="flex items-center gap-2 text-xs text-gray-500">
																{person.email && (
																	<a
																		href={`mailto:${person.email}`}
																		className="text-indigo-600 hover:text-indigo-700"
																		onClick={(e) => e.stopPropagation()}
																	>
																		<EnvelopeIcon className="w-4 h-4" />
																	</a>
																)}
																{person.phoneNumber && (
																	<a
																		href={`tel:${person.phoneNumber}`}
																		className="text-indigo-600 hover:text-indigo-700"
																		onClick={(e) => e.stopPropagation()}
																	>
																		<PhoneIcon className="w-4 h-4" />
																	</a>
																)}
															</div>
														</div>
													</li>
												))}
											</ul>
										) : (
											<div className="px-6 py-8 text-center">
												<p className="text-sm text-gray-500">No employees</p>
											</div>
										)}
									</div>
								)}
							</div>
							<div className="col-span-3">
								{/* Assets */}
								<div className="bg-white rounded-lg border border-gray-200 mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Assets{' '}
											{contact.assets &&
												contact.assets.length > 0 &&
												`(${contact.assets.length})`}
										</h3>
									</div>
									{contact.assets && contact.assets.length > 0 ? (
										<ul className="divide-y divide-gray-200  m-0 p-0 !mt-0">
											{contact.assets.map((asset) => (
												<li
													key={asset.id}
													className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
													onClick={() =>
														navigate(`/assets/${asset.id}/overview`)
													}
												>
													<div className="flex items-center justify-between">
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-900 truncate">
																{[
																	asset.manufacturer?.name ||
																		asset.manufacturerName,
																	asset.model?.name || asset.modelName,
																]
																	.filter(Boolean)
																	.join(' ') || 'Unnamed Asset'}
															</p>
															<p className="text-xs text-gray-500 truncate">
																{assetDisplayTemplate
																	? parseAssetTemplate(
																			assetDisplayTemplate,
																			asset,
																		)
																	: `${asset.location?.name || 'No location'} · SN: ${asset.serialNumber || 'N/A'}`}
															</p>
														</div>
													</div>
												</li>
											))}
										</ul>
									) : (
										<div className="px-6 py-8 text-center">
											<p className="text-sm text-gray-500">No assets</p>
										</div>
									)}
								</div>

								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Contracts
										</h3>
									</div>
									{contact.contracts && contact.contracts.length > 0 ? (
										<ul className="divide-y divide-gray-200 m-0 p-0 !mt-0">
											{contact.contracts.map((contract) => (
												<li
													key={contract.id}
													className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
													onClick={() =>
														navigate(`/manage/contracts/${contract.id}`)
													}
												>
													<div className="flex items-center justify-between">
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-900 truncate">
																{contract.name || 'Unnamed Contract'}
															</p>
														</div>
													</div>
												</li>
											))}
										</ul>
									) : (
										<div className="px-6 py-8 text-center">
											<p className="text-sm text-gray-500">No contracts</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</MainContent>
	);
};
